import { MessagesSquare } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import { PaginationBar } from '@/components/data-table/pagination-bar';
import { EmptyState } from '@/components/empty-state';
import { SelectInput } from '@/components/form/select-input';
import { PageHeader } from '@/components/page-header';
import { ProductThumb } from '@/components/product-thumb';
import { QueryState } from '@/components/query-state';
import { Page } from '@/components/shell/page';
import { PageToolbar } from '@/components/shell/page-toolbar';
import { StatusBadge } from '@/components/status-badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemMedia,
  ItemTitle,
} from '@/components/ui/item';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { PlatformMark } from '@/features/orders/components/platform-mark';
import { toast } from '@/lib/toast';
import {
  type SupportFilter,
  type SupportPlatform,
  useConversations,
  useQuestions,
  useSupportOverview,
} from '../api/support';
import { WaitBadge } from '../components/wait-badge';
import { WAIT_EDGE } from '../wait-band';

type Kind = 'questions' | 'conversations';

const PAGES: Record<Kind, { title: string; description: string; empty: string }> = {
  questions: {
    title: 'Perguntas',
    description:
      'Perguntas pré-venda nos anúncios, quem espera há mais tempo primeiro. A IA sugere; quem envia é você.',
    empty: 'As perguntas aparecem quando uma loja que oferece perguntas é conectada.',
  },
  conversations: {
    title: 'Pós-venda',
    description:
      'Mensagens dos compradores depois da compra: dúvida, troca, devolução. A IA sugere; quem envia é você.',
    empty: 'As mensagens aparecem quando uma loja que oferece mensagens pós-venda é conectada.',
  },
};

/**
 * Atendimento (ADR-059, ADR-076): perguntas pré-venda e pós-venda em telas separadas, como o dono
 * pediu (o ERP separa do mesmo jeito). Uma aba por plataforma (módulos §0); a fila vem com quem
 * espera há mais tempo primeiro, na cor do prazo da empresa.
 */
export function SupportPage({ kind }: { kind: Kind }) {
  const overview = useSupportOverview();
  const [provider, setProvider] = useState('');
  const page = PAGES[kind];
  const offers = (platform: SupportPlatform) =>
    kind === 'questions' ? platform.questions : platform.messages;
  return (
    <Page size="xl">
      <PageHeader title={page.title} description={page.description} />
      <QueryState
        query={overview}
        isEmpty={(data) => !data.platforms.some(offers)}
        empty={
          <EmptyState
            icon={MessagesSquare}
            title="Nenhuma loja com este atendimento"
            description={page.empty}
          />
        }
      >
        {(data) => {
          const platforms = data.platforms.filter(offers);
          const current = platforms.some((item) => item.provider === provider)
            ? provider
            : platforms[0]!.provider;
          return (
            <Tabs value={current} onValueChange={setProvider}>
              <TabsList>
                {platforms.map((platform) => (
                  <TabsTrigger key={platform.provider} value={platform.provider} className="gap-2">
                    <PlatformMark provider={platform.provider} />
                    {platform.displayName}
                    <Counts
                      counts={
                        kind === 'questions'
                          ? platform.counts.questions
                          : platform.counts.conversations
                      }
                    />
                  </TabsTrigger>
                ))}
              </TabsList>
              {platforms
                .filter((platform) => platform.provider === current)
                .map((platform) => (
                  <PlatformSupport key={platform.provider} platform={platform} kind={kind} />
                ))}
            </Tabs>
          );
        }}
      </QueryState>
    </Page>
  );
}

function PlatformSupport({ platform, kind }: { platform: SupportPlatform; kind: Kind }) {
  const [connectionId, setConnectionId] = useState('');
  const blocked = platform.stores.filter((store) => store.permissionMissing);
  return (
    <div className="mt-4 space-y-4">
      {blocked.map((store) => (
        <Alert key={store.id}>
          <AlertTitle>{store.name}: falta uma permissão da loja</AlertTitle>
          <AlertDescription>
            A loja não autorizou "{store.permissionMissing}" para o troig, e as perguntas e
            mensagens dela não podem ser lidas. Conecte a loja de novo em Integrações para liberar.
          </AlertDescription>
        </Alert>
      ))}
      <SupportList
        kind={kind}
        provider={platform.provider}
        connectionId={connectionId}
        storePicker={
          platform.stores.length > 1 ? (
            <SelectInput
              aria-label="Loja"
              className="w-auto min-w-48"
              value={connectionId}
              emptyLabel="Todas as lojas"
              options={platform.stores.map((store) => ({ value: store.id, label: store.name }))}
              onValueChange={(next) => setConnectionId(next)}
            />
          ) : null
        }
      />
    </div>
  );
}

/** Quantos esperam e, deles, quantos já passaram do prazo crítico. */
function Counts({ counts }: { counts: SupportPlatform['counts']['questions'] }) {
  return (
    <>
      <span className="tabular text-muted-foreground">{counts.total}</span>
      {counts.CRITICAL > 0 && (
        <StatusBadge tone="danger">
          <span className="sr-only">críticas:</span>
          {counts.CRITICAL}
        </StatusBadge>
      )}
    </>
  );
}

/** Número do pedido ou do carrinho, como a plataforma mostra. */
const ORDER_NUMBER = /^[\w-]{1,40}$/;

function SupportList({
  kind,
  provider,
  connectionId,
  storePicker,
}: {
  kind: Kind;
  provider: string;
  connectionId: string;
  storePicker: ReactNode;
}) {
  const [state, setState] = useState<SupportFilter['state']>('OPEN');
  const [page, setPage] = useState(1);
  const [typed, setTyped] = useState('');
  const [search, setSearch] = useState('');
  const filter: SupportFilter = {
    provider,
    state,
    page,
    ...(connectionId ? { connectionId } : {}),
    ...(kind === 'conversations' && search ? { search } : {}),
  };
  const applySearch = (value: string) => {
    const trimmed = value.trim();
    if (trimmed && !ORDER_NUMBER.test(trimmed)) {
      toast.error('Digite só o número do pedido (letras, dígitos e hífen).');
      return;
    }
    setSearch(trimmed);
    setPage(1);
  };
  return (
    <div className="space-y-4">
      <PageToolbar>
        <ToggleGroup
          type="single"
          variant="outline"
          aria-label="Situação"
          value={state}
          onValueChange={(next) => {
            if (!next) return;
            setState(next as SupportFilter['state']);
            setPage(1);
          }}
        >
          <ToggleGroupItem value="OPEN">Aguardando resposta</ToggleGroupItem>
          <ToggleGroupItem value="ANSWERED">Respondidas</ToggleGroupItem>
          {kind === 'conversations' && (
            <>
              <ToggleGroupItem value="CLAIM">Com reclamação</ToggleGroupItem>
              <ToggleGroupItem value="RETURN">Com devolução</ToggleGroupItem>
            </>
          )}
        </ToggleGroup>
        {kind === 'conversations' && (
          <Input
            type="search"
            aria-label="Número do pedido"
            placeholder="Número do pedido"
            className="w-56"
            value={typed}
            onChange={(event) => {
              setTyped(event.target.value);
              // Apagou a busca: volta a lista inteira sem precisar de Enter.
              if (!event.target.value.trim()) applySearch('');
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') applySearch(typed);
            }}
          />
        )}
        {storePicker}
      </PageToolbar>
      {kind === 'questions' ? (
        <QuestionList filter={filter} onPage={setPage} />
      ) : (
        <ConversationList filter={filter} onPage={setPage} />
      )}
    </div>
  );
}

function QuestionList({
  filter,
  onPage,
}: {
  filter: SupportFilter;
  onPage: (page: number) => void;
}) {
  const questions = useQuestions(filter);
  return (
    <>
      <Card>
        <CardContent className="pt-4 sm:pt-6">
          <QueryState
            query={questions}
            isEmpty={(data) => data.items.length === 0}
            empty={
              <EmptyState
                icon={MessagesSquare}
                title={
                  filter.state === 'OPEN'
                    ? 'Nenhuma pergunta esperando'
                    : 'Nenhuma pergunta respondida'
                }
                description={
                  filter.state === 'OPEN'
                    ? 'Tudo respondido. As novas aparecem aqui sozinhas.'
                    : 'As respondidas pela loja aparecem aqui.'
                }
              />
            }
          >
            {(data) => (
              <ItemGroup className="gap-2">
                {data.items.map((question) => (
                  <Item
                    key={question.id}
                    variant="outline"
                    size="sm"
                    className={question.waitBand ? WAIT_EDGE[question.waitBand] : undefined}
                    asChild
                  >
                    <Link to={`/atendimento/perguntas/${question.id}`}>
                      <ItemMedia>
                        <ProductThumb src={question.listing?.coverUrl ?? null} />
                      </ItemMedia>
                      <ItemContent>
                        <ItemTitle className="line-clamp-2">
                          {question.text || '(texto removido pela plataforma)'}
                        </ItemTitle>
                        <ItemDescription>
                          {question.listing?.title ?? 'Anúncio não encontrado no troig'} ·{' '}
                          {question.store}
                        </ItemDescription>
                        {!question.open && question.answerText && (
                          <ItemDescription className="line-clamp-2">
                            Resposta: {question.answerText}
                          </ItemDescription>
                        )}
                      </ItemContent>
                      <ItemActions className="flex-col items-end">
                        <WaitBadge minutes={question.waitMinutes} band={question.waitBand} />
                        {question.hasDraft && <StatusBadge tone="info">rascunho</StatusBadge>}
                        {!question.open && question.answeredInTroig && (
                          <StatusBadge tone="success">pelo troig</StatusBadge>
                        )}
                      </ItemActions>
                    </Link>
                  </Item>
                ))}
              </ItemGroup>
            )}
          </QueryState>
        </CardContent>
      </Card>
      {questions.data && (
        <PaginationBar
          page={filter.page}
          pageSize={questions.data.pageSize}
          total={questions.data.total}
          onPageChange={onPage}
        />
      )}
    </>
  );
}

const CONVERSATION_EMPTY: Record<SupportFilter['state'], string> = {
  OPEN: 'Nenhuma mensagem esperando',
  ANSWERED: 'Nenhuma conversa respondida',
  CLAIM: 'Nenhuma conversa de venda com reclamação',
  RETURN: 'Nenhuma conversa de venda com devolução',
};

function ConversationList({
  filter,
  onPage,
}: {
  filter: SupportFilter;
  onPage: (page: number) => void;
}) {
  const conversations = useConversations(filter);
  return (
    <>
      <Card>
        <CardContent className="pt-4 sm:pt-6">
          <QueryState
            query={conversations}
            isEmpty={(data) => data.items.length === 0}
            empty={
              <EmptyState
                icon={MessagesSquare}
                title={
                  filter.search
                    ? `Nenhuma conversa do pedido ${filter.search}`
                    : CONVERSATION_EMPTY[filter.state]
                }
                description="As mensagens dos compradores depois da compra aparecem aqui."
              />
            }
          >
            {(data) => (
              <ItemGroup className="gap-2">
                {data.items.map((conversation) => (
                  <Item
                    key={conversation.id}
                    variant="outline"
                    size="sm"
                    className={conversation.waitBand ? WAIT_EDGE[conversation.waitBand] : undefined}
                    asChild
                  >
                    <Link to={`/atendimento/conversas/${conversation.id}`}>
                      <ItemContent>
                        <ItemTitle className="line-clamp-2">
                          {conversation.lastMessage
                            ? `${conversation.lastMessage.fromSeller ? 'Você: ' : ''}${conversation.lastMessage.text}`
                            : 'Conversa sem mensagens'}
                        </ItemTitle>
                        <ItemDescription>
                          {conversation.order
                            ? `${conversation.order.items.map((item) => `${item.quantity}× ${item.title}`).join(', ')} · ${conversation.order.stageLabel}`
                            : `Venda ${conversation.externalId}`}{' '}
                          · {conversation.store}
                        </ItemDescription>
                      </ItemContent>
                      <ItemActions className="flex-col items-end">
                        <WaitBadge
                          minutes={conversation.waitMinutes}
                          band={conversation.waitBand}
                        />
                        {conversation.blocked && (
                          <StatusBadge tone="neutral">bloqueada</StatusBadge>
                        )}
                        {conversation.hasDraft && <StatusBadge tone="info">rascunho</StatusBadge>}
                      </ItemActions>
                    </Link>
                  </Item>
                ))}
              </ItemGroup>
            )}
          </QueryState>
        </CardContent>
      </Card>
      {conversations.data && (
        <PaginationBar
          page={filter.page}
          pageSize={conversations.data.pageSize}
          total={conversations.data.total}
          onPageChange={onPage}
        />
      )}
    </>
  );
}
